(ns app.front.ui.contextual-menu
  (:require ["@carbon/icons-react/es/overflow-menu--vertical/24" :default OverflowMenu]
            [app.front.ui :as ui]
            [app.front.ui.dropdown :as ui-dd]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.util.util :as util]
            [reagent.core :as r]
            [schema-tools.core :as st]
            [schema.core :as s]))

(s/defschema NotEmptyString
  (s/constrained s/Str (complement str/blank?)))

(s/defschema OptionalActionKeys
  (st/optional-keys
    {:icon             s/Any
     :icon-description NotEmptyString
     :confirm?         s/Bool
     :confirm-content  s/Any
     :data-cy          NotEmptyString
     ;; Per-action CSS class override. Merges over `button-defaults` class-name
     ;; so callers can restyle individual actions (e.g. wizard mobile menu).
     ;; Workaround until we migrate away from Carbon Components.
     :class-name       NotEmptyString}))

(s/defschema OnClickAction
  (st/merge
    {:on-click (s/pred fn?)
     :title    NotEmptyString}
    OptionalActionKeys))

(s/defschema HrefAction
  (st/merge
    {:href  NotEmptyString
     :title NotEmptyString}
    OptionalActionKeys))

(s/defschema Action
  "An individual clickable item in the menu."
  (s/conditional
    :on-click OnClickAction
    :href     HrefAction))

(s/defschema Section
  "A `Section` groups one or more related `Actions` together.
   Only sections with at least one non-nil action are shown."
  (st/merge
    {:actions [(s/maybe Action)]}
    (st/optional-keys
      {:title NotEmptyString})))

(s/defschema Content
  "The main `Content` of the menu."
  [Section])

(def button-defaults {:kind             :ghost
                      :icon-position    :left
                      :icon-description ""
                      :class-name       "min-h-0 pl-2 pt-2 pb-2 text-ui-night-sky border-0 rounded-none normal-case place-content-start w-full"})

(s/defn make-action
  [{:keys [on-request-modal]} {:keys [title confirm? class-name] :as action} :- Action]
  (cc/button (merge
               button-defaults
               (st/select-schema action cc/ButtonOptions)
               (when class-name {:class-name class-name})
               (when (and confirm? on-request-modal)
                 {:on-click #(on-request-modal action)}))
             title))

(s/defn make-section
  [{:keys [on-request-modal]} {:keys [actions title]} :- Section]
  (let [children (->> actions
                      (remove nil?)
                      (mapv (partial make-action (util/mmap on-request-modal))))]
    (when (seq children)
      [:div.flex.flex-col.border-solid.items-start
       (when (some? title)
         [:span.cursor-default.pl-2.pt-2.pb-2.text-ui-dark-gray title])
       (into [:<>] children)])))

(defn to-sections
  ([content] (to-sections {} content))
  ([ctx content]
   (->> content (keep (partial make-section ctx)) seq)))

(defn- has-modal? [content]
  (->> content
       (some (fn [section]
               (->> section :actions (some :confirm?))))))

(defn modal [{:keys [tr on-close open?]} {:keys [title on-click confirm-content]}]
  [ui/confirm-modal
   (util/mmap {:on-yes-click on-click}
              tr on-close open?)
   (or confirm-content
       [:div title])])

(s/defn menu
  "A contextual dropdown menu of actions.

   Provide `tr` if you use `:confirm?`.
   `:options-props` is forwarded to the dropdown options container — use
   `:style` there to override the default downward placement.
   `:content-class` is appended to the inner content wrapper.
   `:close-toggle-element` replaces the toggle icon while the menu is open."
  [content :- Content
   {:keys [content-class data-cy align toggle-props options-props close-toggle-element tr]
    :or   {align "right"}}]
  (r/with-let [*modal-action    (r/atom nil)
               on-request-modal (fn [action]
                                  (reset! *modal-action action))]
    (when-let [content-sections (to-sections (util/mmap on-request-modal) content)]
      [:<>
       (when (has-modal? content)
         (when-not tr (throw (ex-info "When using menu with confirm dialog, tr is required"
                                      (util/mmap data-cy))))
         [modal
          (util/mmap {:on-close #(reset! *modal-action nil)
                      :open?    (some? @*modal-action)}
                     tr)
          @*modal-action])
       (let [aria-label       (-> toggle-props :aria-label)
             open-menu-label  (or aria-label
                                  (and tr (tr [:open-menu]))
                                  (do
                                    (when ^boolean js/goog.DEBUG
                                      (throw (ex-info "Please provide tr for accessibility"
                                                      (util/mmap data-cy))))
                                    "open menu"))
             close-menu-label (or aria-label
                                  (and tr (tr [:close-menu]))
                                  "close menu")]
         [ui-dd/dropdown
          {:align                  align
           :open-icon-description  open-menu-label
           :close-icon-description close-menu-label
           :toggle-props           (r/merge-props
                                     toggle-props
                                     {:class      "shadow-none"
                                      :omit-icon? true
                                      :data-cy    data-cy})
           :close-toggle-element   close-toggle-element
           :options-props          (r/merge-props
                                     options-props
                                     {:class "bg-ui-white w-max filter drop-shadow-xl max-h-52"})}
          [:> OverflowMenu {}]
          (into [:div {:class (str "flex flex-col divide-y divide-gray-500"
                                   (when content-class (str " " content-class)))}]
                content-sections)])])))

(defn menu-or-button
  "Like `menu`, but renders as a button if a single action is given"
  [content {:keys [tr] :as props}]
  (r/with-let [*modal-open? (r/atom false)]
    (if (or (->> content (mapcat :actions) count (not= 1))
            (->> content (mapcat :actions) first :icon nil?)
            (-> content has-modal? not))
      ;; ordinary case, fall back to menu
      [menu content props]

      ;; confirmable single action, show button
      (let [{:keys [title icon data-cy]
             :as   action}              (->> content (mapcat :actions) first)]
        [:<>
         [modal
          (util/mmap {:on-close #(reset! *modal-open? false)
                      :open?    @*modal-open?}
                     tr)
          action]
         [ui-dd/toggle-button
          (util/mmap {:on-click (fn [ev]
                                  (.preventDefault ev)
                                  (reset! *modal-open? true))}
                     data-cy)
          (cc/icon {:icon icon :description title})]]))))

(s/defn has-actions?
  "Return true if the given menu content has at least one action present."
  [content :- Content]
  (->> content
       (mapcat :actions)
       (remove nil?)
       seq
       boolean))
